import { create } from 'zustand';
import type { TrackedSubscription } from '@prisma/client';

interface SubscriptionFilters {
  search: string;
  category?: string;
  tags: string[];
  showInactive: boolean;
}

interface SubscriptionState {
  subscriptions: TrackedSubscription[];
  filters: SubscriptionFilters;
  isLoading: boolean;

  // Actions
  setSubscriptions: (subs: TrackedSubscription[]) => void;
  addSubscription: (sub: TrackedSubscription) => void;
  updateSubscription: (id: string, data: Partial<TrackedSubscription>) => void;
  removeSubscription: (id: string) => void;
  setFilter: <K extends keyof SubscriptionFilters>(key: K, value: SubscriptionFilters[K]) => void;
  resetFilters: () => void;
  setLoading: (loading: boolean) => void;
}

const defaultFilters: SubscriptionFilters = {
  search: '',
  category: undefined,
  tags: [],
  showInactive: true,
};

export const useSubscriptionStore = create<SubscriptionState>((set) => ({
  subscriptions: [],
  filters: defaultFilters,
  isLoading: false,

  setSubscriptions: (subs) => set({ subscriptions: subs, isLoading: false }),

  addSubscription: (sub) => set((state) => ({
    subscriptions: [...state.subscriptions, sub]
  })),

  updateSubscription: (id, data) => set((state) => ({
    subscriptions: state.subscriptions.map(s =>
      s.id === id ? { ...s, ...data, updatedAt: new Date() } : s
    )
  })),

  removeSubscription: (id) => set((state) => ({
    subscriptions: state.subscriptions.filter(s => s.id !== id)
  })),

  setFilter: (key, value) => set((state) => ({
    filters: { ...state.filters, [key]: value }
  })),

  resetFilters: () => set({ filters: defaultFilters }),

  setLoading: (loading) => set({ isLoading: loading }),
}));

// Computed selectors
export const selectFilteredSubscriptions = (state: SubscriptionState) => {
  const { subscriptions, filters } = state;

  return subscriptions.filter(sub => {
    if (!sub) {
      return false;
    }

    // Filter by active status
    if (!filters.showInactive && !sub.isActive) return false;

    // Filter by search
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesName = sub.name.toLowerCase().includes(searchLower);
      const matchesProvider = sub.provider.toLowerCase().includes(searchLower);
      if (!matchesName && !matchesProvider) return false;
    }

    // Filter by category
    if (filters.category && sub.category !== filters.category) return false;

    // Filter by tags
    if (filters.tags.length > 0) {
      const subTags = sub.tags || [];
      const hasMatchingTag = filters.tags.some(tag => subTags.includes(tag));
      if (!hasMatchingTag) return false;
    }

    return true;
  });
};

export const selectMonthlyTotal = (state: SubscriptionState) => {
  return state.subscriptions
    .filter((s): s is TrackedSubscription => Boolean(s && s.isActive))
    .reduce((sum, sub) => {
      const cost = Number(sub.cost);
      // Convert to monthly equivalent
      switch (sub.billingCycle) {
        case 'WEEKLY':
          return sum + (cost * 52 / 12);
        case 'MONTHLY':
          return sum + cost;
        case 'QUARTERLY':
          return sum + (cost / 3);
        case 'YEARLY':
          return sum + (cost / 12);
        default:
          return sum + cost;
      }
    }, 0);
};

export const selectQuota = (state: SubscriptionState, userTier: 'FREE' | 'PRO') => {
  const used = state.subscriptions.filter(s => s?.isActive).length;
  const limit = userTier === 'FREE' ? 2 : Infinity;
  const percentage = userTier === 'FREE' ? Math.round((used / limit) * 100) : 0;
  const isAtLimit = used >= limit;

  return { used, limit, percentage, isAtLimit };
};
